import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Crown, Sparkles, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIPSubscriptionCard = () => {
  const [vipStatus, setVipStatus] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statusResp, configResp] = await Promise.all([
        axios.get(`${API}/payments/vip/status`, { withCredentials: true }),
        axios.get(`${API}/payments/config`),
      ]);
      setVipStatus(statusResp.data);
      setConfig(configResp.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async () => {
    setRedirecting(true);
    try {
      const resp = await axios.post(`${API}/payments/preference/vip`, {}, { withCredentials: true });
      if (resp.data?.init_point) {
        toast.success('Redirigiendo a MercadoPago...');
        window.location.href = resp.data.init_point;
        return;
      }
      throw new Error('No init_point');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No pudimos iniciar el pago');
      setRedirecting(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200/60">
        <CardContent className="pt-6 animate-pulse">
          <div className="h-6 bg-white/60 rounded w-1/3 mb-3"></div>
          <div className="h-4 bg-white/60 rounded w-2/3"></div>
        </CardContent>
      </Card>
    );
  }

  const isActive = vipStatus?.active;

  if (isActive) {
    return (
      <Card className="bg-gradient-to-br from-yellow-50 via-orange-50 to-white border-yellow-300/60" data-testid="vip-active-card">
        <CardHeader>
          <CardTitle className="text-xl font-heading text-[#0A0A0A] flex items-center gap-2">
            <Crown className="w-6 h-6 text-yellow-600" />
            Sos miembro <span className="text-yellow-700">VIP</span>
            <span className="inline-flex items-center gap-1 ml-2 px-2 py-0.5 bg-yellow-200/70 text-yellow-900 text-xs font-bold rounded-full">
              <Sparkles className="w-3 h-3" />
              ACTIVO
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-[#525252]">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span>Acceso anticipado a eventos</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#525252]">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span>Badge VIP en tu perfil</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-[#525252]">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span>Notificaciones premium</span>
          </div>
          {vipStatus?.end_date && (
            <div className="text-xs text-[#737373] pt-2 border-t border-yellow-200/60 mt-2">
              Vence el <strong className="text-[#0A0A0A]">{format(new Date(vipStatus.end_date), "d 'de' MMMM, yyyy", { locale: es })}</strong>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-gradient-to-br from-yellow-50 via-orange-50 to-white border-yellow-300/60" data-testid="vip-upgrade-card">
      <CardHeader>
        <CardTitle className="text-xl font-heading text-[#0A0A0A] flex items-center gap-2">
          <Crown className="w-6 h-6 text-yellow-600" />
          Convertite en <span className="text-yellow-700">VIP</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-[#525252]">
          Pasá al siguiente nivel y desbloqueá beneficios exclusivos por 30 días:
        </p>
        <ul className="space-y-2">
          <li className="flex items-center gap-2 text-sm text-[#0A0A0A]">
            <Sparkles className="w-4 h-4 text-yellow-600 flex-shrink-0" />
            Acceso anticipado a entradas
          </li>
          <li className="flex items-center gap-2 text-sm text-[#0A0A0A]">
            <Sparkles className="w-4 h-4 text-yellow-600 flex-shrink-0" />
            Badge VIP visible en tu perfil y comentarios
          </li>
          <li className="flex items-center gap-2 text-sm text-[#0A0A0A]">
            <Sparkles className="w-4 h-4 text-yellow-600 flex-shrink-0" />
            Notificaciones premium de eventos curados
          </li>
        </ul>
        <div className="flex items-center justify-between pt-2 border-t border-yellow-200/60">
          <div>
            <div className="text-3xl font-bold text-[#0A0A0A]" data-testid="vip-price">
              ARS {config?.vip_price_ars?.toLocaleString('es-AR') || '2.500'}
            </div>
            <div className="text-xs text-[#737373]">por 30 días</div>
          </div>
          <Button
            onClick={handleSubscribe}
            disabled={redirecting}
            className="bg-yellow-600 hover:bg-yellow-700 text-white font-semibold"
            data-testid="vip-subscribe-button"
          >
            <Crown className="w-4 h-4 mr-2" />
            {redirecting ? 'Redirigiendo...' : 'Suscribirme'}
          </Button>
        </div>
        <p className="text-[10px] text-[#737373] text-center">
          Pago seguro vía MercadoPago — tarjeta, débito o billetera virtual
        </p>
      </CardContent>
    </Card>
  );
};

export default VIPSubscriptionCard;
